import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { Plus, AlertTriangle, ClipboardList, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import SeatMemoEditor from './SeatMemoEditor';
import SeatMemoCard, { RESULT_COLORS, Tag } from '../components/SeatMemoCard';

const card = {
  background: 'var(--field)',
  border: '1px solid var(--rule-strong)',
  boxShadow: 'none',
  borderRadius: 16,
  padding: '16px 18px',
  marginBottom: 10,
};

const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
const shiftDate = (d, n) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);

const WD = ['日', '月', '火', '水', '木', '金', '土'];
const dateLabel = (d) => {
  const dt = new Date(`${d}T00:00:00Z`);
  return `${dt.getUTCMonth() + 1}月${dt.getUTCDate()}日(${WD[dt.getUTCDay()]})`;
};

export default function SeatMemoList({ user }) {
  const [memos, setMemos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false); // 読み込み失敗（0件と区別する）
  const [editor, setEditor] = useState(null); // null | 'new' | memo-object
  const [today, setToday] = useState(todayJst());
  // 見ている日。既定は今日だが、前日ぶんの入れ忘れを直せるように過去日へ移動できる（ADR 0025）。
  const [viewDate, setViewDate] = useState(todayJst());
  // 対応待ち＝キャッチャーが着座させ、まだ結果が入っていないメモ（チーム全体・ADR 0014）。
  const [pending, setPending] = useState([]);
  const isCatcher = (user?.job_type || 'both') === 'catcher';
  const isToday = viewDate === today;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, pend] = await Promise.all([
        api.getSeatMemos({ date_from: viewDate, date_to: viewDate }),
        // 対応待ちは「今まさに拾う」ための一覧なので、今日を見ているときだけ取る。
        // キャッチャー本人にも出さない（拾うのはクローザー側の作業なので）。
        (isToday && !isCatcher)
          ? api.getSeatMemos({ date_from: viewDate, date_to: viewDate, pending: 1 })
          : Promise.resolve([]),
      ]);
      setMemos(data);
      setPending(Array.isArray(pend) ? pend : []);
      setError(false);
    } catch {
      // 通信失敗を「0件」と誤表示しない（電波の悪い会場で入力済みが消えたと誤解させない）
      setError(true);
    } finally { setLoading(false); }
  }, [viewDate, isToday, isCatcher]);

  useEffect(() => { load(); }, [load]);

  // 日付またぎ対策：PWAを開いたまま/バックグラウンド復帰で日付が変わったら「今日」を更新する。
  // 見ている日を動かすのは、それまで今日を見ていたときだけ。
  // 過去日を開いている最中に勝手に今日へ飛ばされると、入力中の是正作業が中断してしまう。
  useEffect(() => {
    const check = () => {
      const t = todayJst();
      setToday((prev) => {
        if (t === prev) return prev;
        setViewDate((v) => (v === prev ? t : v));
        return t;
      });
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, []);

  const handleDelete = async (memo) => {
    if (!window.confirm('このメモを削除しますか？')) return;
    try { await api.deleteSeatMemo(memo.id); load(); } catch (e) { alert(e.message); }
  };

  const summary = memos.reduce((acc, m) => {
    acc[m.result] = (acc[m.result] || 0) + 1;
    return acc;
  }, {});
  // 有効商談の件数（ADR 0019）。未選択は数えない。
  const validCount = memos.filter(m => m.valid_deal === 1).length;

  return (
    // 下の余白は「ボトムナビ + 追加ボタン(FAB)」ぶん。120 だと最後のカードに FAB が重なる。
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      {/* ヘッダー */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>{isToday ? '今日' : dateLabel(viewDate)}</div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--ink)' }}>着座メモ</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {validCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: 'var(--gain-bg)', borderRadius: 8, border: '1px solid #1E5540' }}>
              <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--gain)' }}>有効商談 {validCount}</span>
            </div>
          )}
          {['獲得','失注','保留','後追い','未対応'].map(r => summary[r] ? (
            <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: `color-mix(in srgb, ${RESULT_COLORS[r]} 9%, transparent)`, borderRadius: 8, border: `1px solid color-mix(in srgb, ${RESULT_COLORS[r]} 27%, transparent)` }}>
              <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: RESULT_COLORS[r] }}>{r} {summary[r]}</span>
            </div>
          ) : null)}
        </div>
      </div>

      {/* 日付の切替（前日の入れ忘れを直すため。ADR 0025） */}
      <div style={{ ...card, padding: '10px 12px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setViewDate(d => shiftDate(d, -1))} title="前の日" style={navBtn}>
          <ChevronLeft size={18} color="var(--ink-soft)" />
        </button>
        <input
          type="date"
          value={viewDate}
          max={today}
          onChange={e => { if (e.target.value) setViewDate(e.target.value); }}
          style={{ flex: '1 1 150px', minWidth: 140, padding: '10px 12px', minHeight: 44, background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, color: 'var(--ink)', fontSize: 14 }}
        />
        <button onClick={() => setViewDate(d => shiftDate(d, 1))} disabled={isToday} title="次の日"
          style={{ ...navBtn, opacity: isToday ? 0.35 : 1, cursor: isToday ? 'default' : 'pointer' }}>
          <ChevronRight size={18} color="var(--ink-soft)" />
        </button>
        {!isToday && (
          <button onClick={() => setViewDate(today)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', minHeight: 44, borderRadius: 10, border: '1.5px solid #33406E', background: 'var(--mark-soft)', color: 'var(--mark)', fontWeight: 700, fontSize: 'var(--t-note)', cursor: 'pointer' }}>
            <RotateCcw size={14} /> 今日へ戻る
          </button>
        )}
      </div>

      {!isToday && (
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--hold)', background: 'var(--hold-bg)', border: '1px solid #5C4718', borderRadius: 10, padding: '10px 12px', marginBottom: 16, lineHeight: 1.6 }}>
          過去の日（{dateLabel(viewDate)}）を見ています。ここで追加・編集すると、この日のぶんとして記録されます。
        </div>
      )}

      {/* 対応待ち（クローザーが拾って結果を入れる）。今日を見ているときだけ。 */}
      {isToday && !isCatcher && pending.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--hold)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            対応待ちの着座（{pending.length}件）
          </div>
          <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginBottom: 10, lineHeight: 1.6 }}>
            キャッチャーが着座させて、まだ結果が入っていないお客さんです。
            タップして結果を入れると、その獲得はあなたの実績になります。
            「あなた宛」は名指しで渡された着座で、他の人には表示されません。
          </div>
          {pending.map(m => (
            <div
              key={m.id}
              onClick={() => setEditor(m)}
              style={{
                ...card,
                border: '1px solid #5C4718',
                background: 'var(--hold-bg)',
                cursor: 'pointer', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--hold)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{m.memo_time || '時刻なし'}　着座：{m.user_name || `#${m.user_id}`}</span>
                  {/* 指名されたメモは本人にしか出ないが、拾ってよい根拠が見えるようにバッジを出す（ADR 0015） */}
                  {m.assigned_closer_id === user?.id && (
                    <span style={{ fontSize: 'var(--t-note)', fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: 'var(--hold-bg)', color: 'var(--hold)' }}>あなた宛</span>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {m.customer_gender && <Tag>{m.customer_gender}</Tag>}
                  {m.current_power_company && <Tag>{m.current_power_company}</Tag>}
                  {m.household_type && <Tag>{m.household_type}</Tag>}
                  {m.meter_status && <Tag color={m.meter_status === 'なし' ? 'color-mix(in srgb, var(--loss) 27%, transparent)' : undefined}>{m.meter_status}</Tag>}
                  {m.booth_name && <Tag>{m.booth_name}</Tag>}
                </div>
                {m.note && <div style={{ marginTop: 6, fontSize: 'var(--t-note)', color: 'var(--ink-soft)', lineHeight: 1.5 }}>{m.note}</div>}
              </div>
              <span style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--hold)', flexShrink: 0 }}>結果を入れる</span>
            </div>
          ))}
        </div>
      )}

      {/* メモ一覧 */}
      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-soft)', padding: 40 }}>読み込み中...</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <AlertTriangle size={28} color="var(--loss)" style={{ marginBottom: 'var(--s3)' }} />
          <div style={{ fontSize: 14, color: 'var(--loss)', fontWeight: 700 }}>読み込みに失敗しました</div>
          <div style={{ fontSize: 'var(--t-note)', marginTop: 6, color: 'var(--ink-soft)', lineHeight: 1.5 }}>電波の良い場所でもう一度お試しください。<br />（入力済みのメモが消えたわけではありません）</div>
          <button onClick={load} style={{ marginTop: 16, padding: '10px 20px', borderRadius: 10, minHeight: 44, border: '1.5px solid #33406E', background: 'var(--mark-soft)', color: 'var(--mark)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>再読み込み</button>
        </div>
      ) : memos.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-soft)', padding: 60 }}>
          <ClipboardList size={28} color="var(--ink-mute)" style={{ marginBottom: 'var(--s3)' }} />
          <div style={{ fontSize: 14 }}>{isToday ? '本日の着座メモはまだありません' : `${dateLabel(viewDate)} の着座メモはありません`}</div>
          <div style={{ fontSize: 'var(--t-note)', marginTop: 6, color: 'var(--ink-soft)' }}>{isToday ? '着座したらすぐに記録しよう' : '＋ から、この日のぶんを追加できます'}</div>
        </div>
      ) : (
        memos.map(memo => (
          <SeatMemoCard
            key={memo.id}
            memo={memo}
            user={user}
            onEdit={() => setEditor(memo)}
            // 削除できるのは着座させた本人だけ（渡された着座を消させない）。
            // 管理者が他人のメモを消すのは「着座メモ 履歴」から（ADR 0025）。
            onDelete={memo.user_id === user?.id ? handleDelete : undefined}
          />
        ))
      )}

      {/* FAB */}
      <button
        onClick={() => setEditor('new')}
        style={{
          position: 'fixed', right: 20,
          bottom: 'calc(86px + env(safe-area-inset-bottom, 0px))',
          width: 58, height: 58, borderRadius: '50%',
          background: 'var(--grad-primary)',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px var(--mark)',
          transition: 'transform 120ms',
        }}
        onMouseDown={e => e.currentTarget.style.transform = 'scale(0.92)'}
        onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        <Plus size={26} color="#fff" />
      </button>

      {/* エディタ */}
      {editor !== null && (
        <SeatMemoEditor
          user={user}
          memo={editor === 'new' ? null : editor}
          defaultDate={viewDate}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); }}
        />
      )}
    </div>
  );
}

const navBtn = {
  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
  background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
